'use client';

import { useEffect, useRef } from 'react';

import { AudioPlayEvent } from './AudioPlayEvent';

export interface UseAudioCoordinationOptions {
    /** Ref containing the unique identifier for this audio player instance */
    instanceIdRef: React.RefObject<string>;
    /** Callback to pause this audio source */
    onPause: () => void;
}

/**
 * Coordinates audio playback across components to ensure only one audio plays at a time.
 *
 * **Why instance IDs?**
 * Multiple UI components can play the same audio URL (e.g., a VoiceCard and TextInputPlayButton
 * both displaying the same selected voice). Using URLs alone wouldn't distinguish between them,
 * so each player gets a unique instance ID.
 *
 * **When does it pause?**
 * Listens for global `AudioPlayEvent` dispatched when any audio starts playing.
 * If the event's `instanceId` differs from this hook's `instanceId`, it calls `onPause()`
 * to stop playback—ensuring the newly-started audio is the only one playing.
 *
 * Uses a ref for `onPause` to avoid effect churn when callers pass inline functions.
 */
export const useAudioCoordination = ({ instanceIdRef, onPause }: UseAudioCoordinationOptions): void => {
    // Store callback in ref to avoid re-running the event listener effect when callback changes
    const onPauseRef = useRef(onPause);

    // Sync the ref in an effect — safe because the ref is only read inside event handlers
    useEffect(() => {
        onPauseRef.current = onPause;
    }, [onPause]);

    useEffect(() => {
        const handleAudioPlay = (evt: Event): void => {
            const audioEvent = evt as AudioPlayEvent;
            // Pause if another instance started playing (different instanceId)
            if (audioEvent.instanceId !== instanceIdRef.current) {
                onPauseRef.current();
            }
        };

        window.addEventListener(AudioPlayEvent.type, handleAudioPlay);

        return () => {
            window.removeEventListener(AudioPlayEvent.type, handleAudioPlay);
        };
    }, [instanceIdRef]);
};
